import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  collection, 
  doc, 
  updateDoc, 
  getDoc,
  setDoc 
} from 'firebase/firestore';
import { db } from '../../firebase';

const EventDiscountManager = ({ eventId, onClose }) => {
  const { hasRole } = useAuth();
  const [loading, setLoading] = useState(false);
  const [discountCodes, setDiscountCodes] = useState([]);
  const [newCode, setNewCode] = useState({
    code: '',
    discount: '',
    type: 'percentage',
    description: '',
    maxUses: '',
    validUntil: ''
  });

  const isAdmin = hasRole('admin');

  useEffect(() => {
    if (eventId) {
      loadEventDiscountCodes();
    }
  }, [eventId]);

  const loadEventDiscountCodes = async () => {
    try {
      const eventDoc = await getDoc(doc(db, 'events', eventId));
      if (eventDoc.exists()) {
        const eventData = eventDoc.data();
        setDiscountCodes(eventData.discountCodes || []);
      }
    } catch (error) {
      console.error('Error loading discount codes:', error);
    }
  };

  const handleAddDiscountCode = async () => {
    if (!newCode.code || !newCode.discount) {
      alert('Please fill in code and discount amount');
      return;
    }

    setLoading(true);
    try {
      const updatedCodes = [...discountCodes, {
        ...newCode,
        code: newCode.code.toUpperCase(),
        discount: parseFloat(newCode.discount),
        maxUses: newCode.maxUses ? parseInt(newCode.maxUses) : null,
        usedCount: 0,
        createdAt: new Date(),
        active: true
      }];

      await updateDoc(doc(db, 'events', eventId), {
        discountCodes: updatedCodes
      });

      setDiscountCodes(updatedCodes);
      setNewCode({
        code: '',
        discount: '',
        type: 'percentage',
        description: '',
        maxUses: '',
        validUntil: ''
      });
      
      alert('Discount code added successfully!');
    } catch (error) {
      console.error('Error adding discount code:', error);
      alert('Failed to add discount code');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleCode = async (index) => {
    setLoading(true);
    try {
      const updatedCodes = [...discountCodes];
      updatedCodes[index].active = !updatedCodes[index].active;

      await updateDoc(doc(db, 'events', eventId), {
        discountCodes: updatedCodes
      });

      setDiscountCodes(updatedCodes);
    } catch (error) {
      console.error('Error toggling discount code:', error);
      alert('Failed to update discount code');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCode = async (index) => {
    if (!window.confirm('Are you sure you want to delete this discount code?')) {
      return;
    }

    setLoading(true);
    try {
      const updatedCodes = discountCodes.filter((_, i) => i !== index);

      await updateDoc(doc(db, 'events', eventId), {
        discountCodes: updatedCodes
      });

      setDiscountCodes(updatedCodes);
      alert('Discount code deleted successfully!');
    } catch (error) {
      console.error('Error deleting discount code:', error);
      alert('Failed to delete discount code');
    } finally {
      setLoading(false);
    }
  };

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Event Discount Codes</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Add New Discount Code */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <h3 className="text-lg font-semibold mb-4">Add New Discount Code</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Code
              </label>
              <input
                type="text"
                value={newCode.code}
                onChange={(e) => setNewCode({...newCode, code: e.target.value.toUpperCase()})}
                placeholder="ADMIN50"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Discount Amount
              </label>
              <div className="flex">
                <input
                  type="number"
                  value={newCode.discount}
                  onChange={(e) => setNewCode({...newCode, discount: e.target.value})}
                  placeholder="50"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                />
                <select
                  value={newCode.type}
                  onChange={(e) => setNewCode({...newCode, type: e.target.value})}
                  className="px-3 py-2 border border-gray-300 rounded-r-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                >
                  <option value="percentage">%</option>
                  <option value="fixed">$</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <input
                type="text"
                value={newCode.description}
                onChange={(e) => setNewCode({...newCode, description: e.target.value})}
                placeholder="Admin discount"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Uses (optional)
              </label>
              <input
                type="number"
                value={newCode.maxUses}
                onChange={(e) => setNewCode({...newCode, maxUses: e.target.value})}
                placeholder="Unlimited"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Valid Until (optional)
              </label>
              <input
                type="date"
                value={newCode.validUntil}
                onChange={(e) => setNewCode({...newCode, validUntil: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={handleAddDiscountCode}
                disabled={loading}
                className="w-full bg-apn-blue text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Adding...' : 'Add Code'}
              </button>
            </div>
          </div>
        </div>

        {/* Existing Discount Codes */}
        <div>
          <h3 className="text-lg font-semibold mb-4">Existing Discount Codes</h3>
          {discountCodes.length === 0 ? (
            <p className="text-gray-500">No discount codes created yet.</p>
          ) : (
            <div className="space-y-3">
              {discountCodes.map((code, index) => (
                <div key={index} className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <span className="font-mono font-bold text-lg">{code.code}</span>
                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm">
                          {code.discount}{code.type === 'percentage' ? '%' : '$'} off
                        </span>
                        <span className={`px-2 py-1 rounded text-sm ${
                          code.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {code.active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <p className="text-gray-600 mt-1">{code.description}</p>
                      <div className="text-sm text-gray-500 mt-2">
                        {code.maxUses && (
                          <span>Used: {code.usedCount || 0}/{code.maxUses} • </span>
                        )}
                        {code.validUntil && (
                          <span>Valid until: {new Date(code.validUntil).toLocaleDateString()}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleToggleCode(index)}
                        disabled={loading}
                        className={`px-3 py-1 rounded text-sm ${
                          code.active 
                            ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                            : 'bg-green-100 text-green-700 hover:bg-green-200'
                        }`}
                      >
                        {code.active ? 'Deactivate' : 'Activate'}
                      </button>
                      <button
                        onClick={() => handleDeleteCode(index)}
                        disabled={loading}
                        className="px-3 py-1 bg-red-100 text-red-700 rounded text-sm hover:bg-red-200"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventDiscountManager;
